/* */

/* eslint-disable no-useless-escape */

import { takeEvery, put, takeLatest, select, delay, race, take } from 'redux-saga/effects';
import Querystring from 'query-string';

import types from 'redux/types';
import Services, { HeaderUTF8Option } from 'services';
import ServletServices from 'services/ServletServices';

import { getNextDataRefetch, isResponseSuccess } from 'helpers';
import hooks from 'hooks';

import { SEND_SERVLET_URL, STATS_SERVLET_URL, INBOX_URL } from 'consts/api';
import { spamColor, stateCampaign, TIME_OUT_CLOSE_TOAST } from 'consts/data';
import { cloneDeep, isString } from 'lodash';
import { showError } from 'helpers/handleError';
import isArray from 'lodash/isArray';
import isEmpty from 'lodash/isEmpty';
import components, { typeMounted, typeUnmounted } from 'consts/components';
import { generateSpamScoreString } from 'helpers/string';
import { handleWorker } from 'helpers/saga';
import { notifySuccess } from 'helpers/handleNotification';
import { requestMultiplePage } from 'helpers/request';
import CampaignModel from 'models/campaign.model';

const {
  REQUEST_API_SERVLET,
  REQUEST_API_SERVLET_SUCCESS,
  REQUEST_API_SERVLET_FAILED,
  REQUEST_REFRESH_SERVLET,
  REQUEST_SEND_INBOX_SERVLET,
  REQUEST_SEND_INBOX_SERVLET_SUCCESS,
  REQUEST_SEND_INBOX_SERVLET_FAILED,
  REQUEST_API_INBOX_DETAIL_SUCCESS,
  REQUEST_SEND_SERVLET,
  REQUEST_SEND_SERVLET_FAILED,
  REQUEST_SEND_SERVLET_SUCCESS,
  REQUEST_SPIN_SERVLET,
  REQUEST_SPIN_SERVLET_FAILED,
  REQUEST_SPIN_SERVLET_SUCCESS,
  REQUEST_PREVIEW_SPIN,
  REQUEST_PREVIEW_SPIN_FAILED,
  REQUEST_PREVIEW_SPIN_SUCCESS,
  REQUEST_HISTORY_BY_ID,
  REQUEST_HISTORY_BY_ID_SUCCESS,
  REQUEST_HISTORY_BY_ID_FAILED,
  REQUEST_MORE_HISTORY,
  REQUEST_MORE_HISTORY_SUCCESS,
  REQUEST_MORE_HISTORY_FAILED,
  REQUEST_PAUSE_LOAD_HISTORY,
  REQUEST_PAUSE_LOAD_HISTORY_SUCCESS,
  REQUEST_PAUSE_LOAD_HISTORY_FAILED,
  REQUEST_CONTINUE_HIST,
  REQUEST_CONTINUE_HIST_FAILED,
  REQUEST_CONTINUE_HIST_SUCCESS,
  REQUEST_MARK_AS_COMPLETED,
  REQUEST_MARK_AS_COMPLETED_SUCCESS,
  REQUEST_MARK_AS_COMPLETED_FAILED,
  REQUEST_UPDATE_MESSAGE_HIST,
  REQUEST_UPDATE_MESSAGE_HIST_SUCCESS,
  REQUEST_UPDATE_MESSAGE_HIST_FAILED,
  REQUEST_FILTER_CAMPAIGN,
  REQUEST_FILTER_CAMPAIGN_SUCCESS,
  REQUEST_FILTER_CAMPAIGN_FAILED,
  IS_LAST_HISTORY,
  REQUEST_UPDATE_CREATIVE_CAMPAIGN_SUCCESS,
  REQUEST_UPDATE_OFFER_CAMPAIGN_SUCCESS,
  REQUEST_UPDATE_DOMAIN_CAMPAIGN_SUCCESS,
  UPDATE_CAMPAIGN,
  REQUEST_CLEAR_PREVIEW_SPIN,
  REQUEST_REFRESH_CAMPAIGN,
  REQUEST_REFRESH_CAMPAIGN_SUCCESS,
  REQUEST_REFRESH_CAMPAIGN_FAILED,
  REQUEST_RESEND_TO_FAILED_CONTACTS,
  CHANGE_FILTER_CAMPAIGN,

  REQUEST_SWITCH_STATUS_ROUTE_HISTORY,
  REQUEST_SWITCH_STATUS_ROUTE_HISTORY_SUCCESS,
  REQUEST_SWITCH_STATUS_ROUTE_HISTORY_FAILED,

  REQUEST_PULL_REVENUE_CAMPAIGN,
  REQUEST_SORTING_CAMPAIGN,
  REQUEST_SORTING_CAMPAIGN_SUCCESS,
  REQUEST_SORTING_CAMPAIGN_FAILED,
  REQUEST_PULL_REVENUE_CAMPAIGN_SUCCESS,
  REQUEST_PULL_REVENUE_CAMPAIGN_FAILED,

  REQUEST_UPDATE_STATUS_CONTACT_GROUP,
  REQUEST_UPDATE_STATUS_CONTACT_GROUP_SUCCESS,
  REQUEST_UPDATE_STATUS_CONTACT_GROUP_FAILED,
} = types;

const { Notification } = hooks;

function* fetchMultiplePageHistory() {
  const filterCampaign = yield select((state) => state.filterReducer.filterCampaign);
  const { offset, limit, statuses } = filterCampaign;
  const page = offset / limit;
  const isRunningCampaignSelected = CampaignModel.checkIsOnlyRunningCampaignSelected(statuses);

  let requestSearchHis;
  const bodySearchHis = CampaignModel.parseFilterSearchHis(filterCampaign);
  if (isRunningCampaignSelected) {
    requestSearchHis = Promise.all([ServletServices.searchHis(bodySearchHis)]);
  } else {
    requestSearchHis = Promise.all(
      requestMultiplePage((eachPage) => {
        bodySearchHis.offset = eachPage * limit;
        return ServletServices.searchHis(bodySearchHis);
      }, Math.ceil(page))
    );
  }

  const reponseSearchHis = yield requestSearchHis;
  const [nextHistory, isLastHistory] = getNextDataRefetch(reponseSearchHis, limit, 'history');
  return { nextHistory, isLastHistory };
}

function* refreshServlet({ refreshPrevious = false } = {}) {
  if (refreshPrevious) {
    const { nextHistory } = yield fetchMultiplePageHistory();

    yield put({
      type: REQUEST_API_SERVLET_SUCCESS,
      historyPhone: {
        history: nextHistory,
      },
    });
    return;
  }

  yield put({ type: REQUEST_REFRESH_SERVLET });
}

function* pauseLoadHistory(data) {
  data.action = 'cancelHist';
  const { toggle, id, action } = data;
  try {
    const res = yield Services.post(
      SEND_SERVLET_URL,
      Querystring.stringify({
        action,
        histId: id,
      })
    );
    if (isResponseSuccess(res.status)) {
      toggle();
      yield getServlet();
      yield put({ type: REQUEST_PAUSE_LOAD_HISTORY_SUCCESS, id });
      Notification('success', 'Campaign has been paused!', '', TIME_OUT_CLOSE_TOAST);
    }
  } catch (error) {
    showError(error, error.toString(), '', TIME_OUT_CLOSE_TOAST, () => {});
    yield put({
      type: REQUEST_PAUSE_LOAD_HISTORY_FAILED,
      error,
      id,
    });
  }
}

function* getMoreHistory() {
  const filterCampaign = yield select((state) => state.filterReducer.filterCampaign);
  const { status, offset, limit } = filterCampaign;

  try {
    const { responseStats, cancelled } = yield race({
      responseStats: ServletServices.searchHis(
        CampaignModel.parseFilterSearchHis({
          ...filterCampaign,
          offset: status === stateCampaign.RUNNING ? 0 : offset,
        })
      ),
      cancelled: take([
        REQUEST_API_SERVLET,
        REQUEST_FILTER_CAMPAIGN,
        typeUnmounted(components.send),
        typeUnmounted(components.monitoring),
      ]),
    });

    if (cancelled) {
      return;
    }

    if (isResponseSuccess(responseStats.status)) {
      if (typeof responseStats.data === 'string' && responseStats.data.toLowerCase() === 'fail') {
        showError('Something went wrong!, Please refresh page and try again');
      }
      if (
        typeof responseStats.data === 'string' &&
        responseStats.data.toLowerCase() === 'notexist'
      ) {
        yield put({
          type: IS_LAST_HISTORY,
          isLastHistory: true,
        });
      }
      if (typeof responseStats.data === 'object') {
        yield put({
          type: REQUEST_MORE_HISTORY_SUCCESS,
          historyPhone: responseStats.data,
          offset,
        });

        if (responseStats.data.history.length < limit) {
          yield put({
            type: IS_LAST_HISTORY,
            isLastHistory: true,
          });
        }
      }
    }
  } catch (error) {
    showError(error, error.toString(), '', TIME_OUT_CLOSE_TOAST, () => {});
    yield put({
      type: REQUEST_MORE_HISTORY_FAILED,
      error,
    });
  }
}

function* pullRevenue() {
  const filterCampaign = yield select((state) => state.filterReducer.filterCampaign);
  const { offset, limit } = filterCampaign;
  try {
    let bodyRevenuePull = {
      ...CampaignModel.parseFilterSearchHis({ ...filterCampaign }),
      offset: 0,
      limit: offset + limit,
    };
    yield ServletServices.pullRevenue(bodyRevenuePull);

    yield delay(200 * limit);
    Notification(
      'success',
      'Revenue is updating, it may take a few minutes',
      '',
      TIME_OUT_CLOSE_TOAST
    );

    yield put({ type: REQUEST_PULL_REVENUE_CAMPAIGN_SUCCESS });
    yield refreshServlet();
  } catch (error) {
    showError(error, error.toString(), 'Error', TIME_OUT_CLOSE_TOAST, () => {});
    yield put({ type: REQUEST_PULL_REVENUE_CAMPAIGN_FAILED, error });
  }
}

function* getServlet() {
  const filterCampaign = yield select((state) => state.filterReducer.filterCampaign);
  const isSendMounted = yield select((state) => state.stateComponent[typeMounted(components.send)]);
  const isMonitoringMounted = yield select(
    (state) => state.stateComponent[typeMounted(components.monitoring)]
  );
  const prevHistory = yield select((state) => state.servletReducer.historyPhone.history);

  const shouldAutoRefetch = isSendMounted || isMonitoringMounted;
  const { offset, statuses, limit } = filterCampaign;
  const isRunningCampaignSelected = CampaignModel.checkIsOnlyRunningCampaignSelected(statuses);

  try {
    /**
     * Status = running => set default offset = 0
     */
    let requestSearchHis;
    const bodySearchHis = CampaignModel.parseFilterSearchHis({
      ...filterCampaign,
      offset: 0,
    });

    if (isRunningCampaignSelected) {
      requestSearchHis = ServletServices.searchHis(bodySearchHis);
    } else {
      const page = offset / limit;
      requestSearchHis = Promise.all(
        requestMultiplePage((eachPage) => {
          bodySearchHis.offset = eachPage * limit;
          return ServletServices.searchHis(bodySearchHis);
        }, Math.ceil(page))
      );
    }

    const { responseStats, cancelled } = yield race({
      responseStats: requestSearchHis,
      cancelled: take([
        REQUEST_API_SERVLET,
        REQUEST_FILTER_CAMPAIGN,
        typeUnmounted(components.send),
        typeUnmounted(components.monitoring),
      ]),
    });

    if (cancelled) {
      return;
    }

    if (isRunningCampaignSelected) {
      const isError =
        typeof responseStats.data === 'string' && responseStats.data.toLowerCase() === 'fail';
      if (isError) {
        showError('FAILED');
        yield put({ type: REQUEST_API_SERVLET_FAILED, error: 'FAIL' });
        yield put({ type: REQUEST_FILTER_CAMPAIGN_FAILED, error: 'FAIL' });
        return;
      }

      // TA-657
      if (
        shouldAutoRefetch &&
        isRunningCampaignSelected &&
        isArray(responseStats.data.history) &&
        isArray(prevHistory)
      ) {
        // If one of campaign is completed -> refetch list DID
        // if (prevHistory.length !== responseStats.data.history.length) {
        //   yield getPhoneNumbers();
        // }
      }

      //* => Put to histories list
      yield put({
        type: REQUEST_API_SERVLET_SUCCESS,
        historyPhone: responseStats.data,
      });

      //* => Check is last history
      yield put({
        type: IS_LAST_HISTORY,
        isLastHistory: false,
      });

      //* Change isFiltering => false
      yield put({ type: REQUEST_FILTER_CAMPAIGN_SUCCESS });
      // yield getCreativeEachCampaign(responseStats.data.history);

      /**
       * If Send Component is mounted and state = running, execute re-get
       */
      if (shouldAutoRefetch && isRunningCampaignSelected) {
        /** If reponse from API is string (error, not existed) => break */
        const isStopRefetch =
          typeof responseStats.data === 'string' ||
          (isArray(responseStats?.data?.history) && isEmpty(responseStats?.data?.history));

        if (!isStopRefetch) {
          yield delay(2000);
          yield refreshServlet();
        }
      }
      return;
    }

    const [nextHistory, isLastHistory] = getNextDataRefetch(responseStats, limit, 'history');

    yield put({
      type: IS_LAST_HISTORY,
      isLastHistory,
    });
    yield put({
      type: REQUEST_API_SERVLET_SUCCESS,
      historyPhone: {
        history: nextHistory,
      },
    });
    yield put({ type: REQUEST_FILTER_CAMPAIGN_SUCCESS });
  } catch (error) {
    showError(error, error.toString(), 'Error', TIME_OUT_CLOSE_TOAST, () => {});
    yield put({ type: REQUEST_API_SERVLET_FAILED, error });
  }
}

// function* getPhoneNumbers() {
//   try {
//     // const twoWayUser = yield select((state) => state.userServletReducer.data.twoWay);
//     // if (twoWayUser !== 2) {
//     //   return;
//     // }

//     const reponsePhoneNumber = yield ServletServices.fetchPhoneNumber();
//     if (isResponseSuccess(reponsePhoneNumber.status)) {
//       if (
//         typeof reponsePhoneNumber.data === 'string' &&
//         reponsePhoneNumber.data.toLowerCase() === 'fail'
//       ) {
//         showError('FAILED');
//         yield put({ type: REQUEST_PHONE_NUMBERS_FAILED, error: 'FAIL' });
//       } else {
//         yield put({
//           type: REQUEST_PHONE_NUMBERS_SUCCESS,
//           phoneNumbers: reponsePhoneNumber.data,
//         });
//       }
//     }
//   } catch (error) {
//     showError(error);
//     yield put({ type: REQUEST_PHONE_NUMBERS_FAILED, error });
//   }
// }

function* getServletById(data) {
  data.action = 'loadHistbyIds';
  const { listIds, action } = data;
  const convertToParamString = listIds.toString().replace(/\,+/gm, '&ids=');
  const urlPost = `${STATS_SERVLET_URL}?action=${action}&ids=${convertToParamString}`;
  try {
    const res = yield Services.post(urlPost);
    if (isResponseSuccess(res.status)) {
      if (typeof res.data === 'object') {
        yield put({
          type: REQUEST_HISTORY_BY_ID_SUCCESS,
          data: res.data,
        });
      }
      if (typeof res.data === 'string' && res.data.toLowerCase() === 'fail') {
        showError('Something went wrong!');
      }
    }
  } catch (error) {
    showError(error);
    yield put({
      type: REQUEST_HISTORY_BY_ID_FAILED,
      error,
    });
  }
}

function* reFetchInboxDetail(data) {
  const { beta, threadUUID } = data;
  try {
    const bodyUpdateDetail = Querystring.stringify({
      action: 'viewThread',
      beta,
      threadUUID,
    });
    const resUpdateDetailInbox = yield Services.post(INBOX_URL, bodyUpdateDetail);
    yield put({
      type: REQUEST_API_INBOX_DETAIL_SUCCESS,
      inboxDetail: resUpdateDetailInbox.data,
      threadUUID,
    });
  } catch (error) {
    showError(error);
    yield put({
      type: REQUEST_SEND_INBOX_SERVLET_FAILED,
      error,
    });
  }
}

function* sendInboxServlet(data) {
  data.action = 'sendThreadMessage';
  const { action, phoneSelected, threadUUID, message, resetForm, callbacks, beta } = data;
  try {
    const body = Querystring.stringify({
      action,
      phoneSelected,
      threadUUID,
      message,
    });
    const res = yield Services.post(SEND_SERVLET_URL, body, HeaderUTF8Option);
    if (isResponseSuccess(res.status)) {
      const { message: errorMsg, externalId } = res.data;

      const success = !isEmpty(externalId);
      if (success) {
        // Add new inbox immediately
        yield put({
          type: types.UPDATE_INBOX_DETAIL,
          payload: {
            threadUUID,
            phoneSelected,
            message,
          },
        });
        yield put({ type: REQUEST_SEND_INBOX_SERVLET_SUCCESS });
        callbacks && callbacks.onSuccess();
        Notification('success', 'Sent message successfully!', '', TIME_OUT_CLOSE_TOAST);
        resetForm();

        yield delay(1000);

        // Refetch to get full info from server
        yield reFetchInboxDetail({ threadUUID, beta });
      } else {
        if (!errorMsg) {
          showError('Can not send reply to this contact');
          yield put({
            type: REQUEST_SEND_INBOX_SERVLET_FAILED,
            error: 'Can not send reply to this contact',
          });
        } else {
          showError(errorMsg);
          yield put({
            type: REQUEST_SEND_INBOX_SERVLET_FAILED,
            error: errorMsg,
          });
        }
      }
    }
  } catch (error) {
    showError(error, error.toString(), '', TIME_OUT_CLOSE_TOAST);
    yield put({
      type: REQUEST_SEND_INBOX_SERVLET_FAILED,
      error,
    });
  }
}

function* sendServlet(data) {
  const { payload = {}, callbacks } = data;

  try {
    const res = yield ServletServices.sendServlet(payload);
    if (isResponseSuccess(res.status)) {
      if (typeof res.data === 'string') {
        if (res.data.toLowerCase() === 'success') {
          // toggle();
          yield delay(3000);
          yield put({ type: REQUEST_SEND_SERVLET_SUCCESS });
          Notification('success', 'Successfully sent!', '', TIME_OUT_CLOSE_TOAST);
          callbacks && callbacks.onSuccess(res);
        } else {
          showError(res.data);
          callbacks && callbacks.onFailed(res);
          yield put({
            type: REQUEST_SEND_SERVLET_FAILED,
            errorSend: 'FAIL SEND MESSAGE',
          });
        }
      }
    }
  } catch (error) {
    showError(error);
    callbacks && callbacks.onFailed(error);
    yield put({
      type: REQUEST_SEND_SERVLET_FAILED,
      errorSend: error,
    });
  }
}

function* spinServlet(data) {
  data.action = 'generateSpinTax';
  const { action, message, setFieldValue } = data;
  try {
    const body = Querystring.stringify({
      message,
      action,
    });
    const res = yield Services.post(SEND_SERVLET_URL, body);
    if (isResponseSuccess(res.status)) {
      if (
        typeof res.data === 'string' &&
        res.data.toLowerCase() !== 'fail' &&
        res.data !== message
      ) {
        Notification('success', 'Successfully spinned!', '', TIME_OUT_CLOSE_TOAST, () => {});
        setFieldValue('message', res.data);
        yield put({ type: REQUEST_SPIN_SERVLET_SUCCESS });
      } else {
        Notification(
          'warning',
          "Can't generate SpinTax. Please contact us for support!",
          '',
          TIME_OUT_CLOSE_TOAST,
          () => {}
        );
        yield put({
          type: REQUEST_SPIN_SERVLET_FAILED,
          error: 'Failed',
        });
      }
    }
  } catch (error) {
    showError(error, error.toString(), 'Error', TIME_OUT_CLOSE_TOAST, () => {});
    yield put({
      type: REQUEST_SPIN_SERVLET_FAILED,
      error,
    });
  }
}

const convertHTMLToPlainText = (a) => {
  return a
    .replace(/<br\s*[\/]?>/gi, '\n')
    .replace(/<br>/gi, '\n')
    .replace(/<[^>]*>/gi, '')
    .replace(/<\/[^>]*>/gi, '')
    .replace(/&nbsp;|&#160;|\xa0/gi, ' ')
    .trim();
};

function* previewSpinTax(data) {
  data.action = 'previewSpinTax';
  const { action, message } = data;

  try {
    const body = Querystring.stringify({
      action,
      message: convertHTMLToPlainText(message),
    });

    const res = yield Services.post(SEND_SERVLET_URL, body, HeaderUTF8Option);
    if (isResponseSuccess(res.status)) {
      const { spinTax } = res?.data || {};

      /** Check response if it is empty -> set to empty array */
      if (!spinTax) {
        yield put({
          type: REQUEST_PREVIEW_SPIN_SUCCESS,
          string: '',
          listStringPreview: [],
        });
        return;
      }

      if (isArray(spinTax)) {
        const listStringPreview = spinTax.map((el) => {
          const { message, spamScore } = el;
          const key = Object.keys(spamScore || {})[0];
          const value = spamScore[key || 0];
          return {
            text: message,
            scoreSpam: generateSpamScoreString({
              color: spamColor[key],
              index: key,
              value: value,
            }),
          };
        });

        yield put({
          type: REQUEST_PREVIEW_SPIN_SUCCESS,
          string: res.data,
          listStringPreview,
        });
      }
    }
  } catch (error) {
    showError(error, error.toString(), 'Error', TIME_OUT_CLOSE_TOAST, () => {});
    yield put({
      type: REQUEST_PREVIEW_SPIN_FAILED,
      error,
    });
  }
}

function* continueHist(data) {
  const { histId, toggle } = data;
  try {
    const resContinue = yield ServletServices.continueHist({ histId });
    if (isResponseSuccess(resContinue.status)) {
      if (typeof resContinue.data === 'string' && resContinue.data.toLowerCase() === 'success') {
        Notification('success', 'Campaign has been resumed!', '', TIME_OUT_CLOSE_TOAST);
        toggle();

        const history = yield select((state) => state.servletReducer.historyPhone.history);
        if (history) {
          yield put({
            type: REQUEST_CONTINUE_HIST_SUCCESS,
            histId,
            history: history.filter((his) => his.id !== histId),
          });
        }
      } else {
        showError('Something went wrong! Please contact us!');
        yield put({
          type: REQUEST_CONTINUE_HIST_FAILED,
          histId,
          error: 'Something went wrong! Please contact us!',
        });
        toggle();
      }
    }
  } catch (error) {
    toggle();
    showError(error, error.toString(), 'Error', TIME_OUT_CLOSE_TOAST, () => {});
    yield put({
      type: REQUEST_CONTINUE_HIST_FAILED,
      histId,
      error,
    });
  }
}

function* markAsCompleted(data) {
  const { cpId, toggle } = data;
  try {
    const resMarkAs = yield ServletServices.markAsCompleted({ cpId });
    if (isResponseSuccess(resMarkAs.status)) {
      const history = yield select((state) => state.servletReducer.historyPhone.history);
      if (history) {
        yield put({
          type: REQUEST_MARK_AS_COMPLETED_SUCCESS,
          history: history.filter((el) => el.id !== cpId),
          cpId,
        });
        toggle();
      }
    }
  } catch (error) {
    toggle();
    showError(error, error.toString(), 'Error', TIME_OUT_CLOSE_TOAST);
    yield put({
      type: REQUEST_MARK_AS_COMPLETED_FAILED,
      cpId,
      error,
    });
  }
}

function* updateMessageHist(data) {
  const { idHist, body, callbacks } = data;

  try {
    const bodyParsed = cloneDeep(body);

    //* TA-8400: remove campaignInfo from updateMessageHist API
    delete bodyParsed.campaignInfo;

    if (bodyParsed?.routes && isArray(bodyParsed?.routes)) {
      bodyParsed.routes = bodyParsed.routes.map((el) => {
        const newEl = cloneDeep(el);
        delete newEl.campaignInfo;
        return newEl;
      });
    }

    const res = yield ServletServices.updateMessageHist(bodyParsed, idHist);
    if (isResponseSuccess(res.status)) {
      // Success
      if (typeof res.data === 'string' && res.data.toLowerCase().includes('successfully')) {
        yield getServlet();
        yield put({
          type: REQUEST_UPDATE_MESSAGE_HIST_SUCCESS,
          idHist,
        });
        Notification('success', 'Updated Successfully!', '', TIME_OUT_CLOSE_TOAST);
        yield callbacks && callbacks?.onSuccess();
      } else {
        if (typeof res.data === 'string') showError(res.data);
        else {
          showError('Something went wrong! Please contact us!');
        }
        yield put({
          type: REQUEST_UPDATE_MESSAGE_HIST_FAILED,
          idHist,
          error: 'Something went wrong! Please contact us!',
        });
        callbacks && callbacks.onFailed();
      }
    }
  } catch (error) {
    showError(error);
    yield put({
      type: REQUEST_UPDATE_MESSAGE_HIST_FAILED,
      idHist,
      error,
    });
    callbacks && callbacks.onFailed();
  }
}

function* updateWhenChangeStatus(data) {
  const { cpId, id, creative, offer, domainGroup } = data;
  const history = yield select((state) => state.servletReducer.historyPhone.history);
  if (history) {
    // Update list
    const tempHistory = cloneDeep(history);
    const campaign = tempHistory.find((cp) => cp.id === cpId);

    if (creative) {
      const indexCreativeInCampaign = campaign.creatives.findIndex((c) => c.id === id);
      campaign.creatives[indexCreativeInCampaign] = {
        ...campaign.creatives[indexCreativeInCampaign],
        ...creative,
        status: creative.status === 1 ? true : false,
      };
    }

    if (offer) {
      const indexOfferInCampaign = campaign.offers.findIndex((o) => o.id === id);
      campaign.offers[indexOfferInCampaign] = {
        ...campaign.offers[indexOfferInCampaign],
        ...offer,
        status: offer.status === 1 ? true : false,
      };
    }

    if (domainGroup) {
      if (campaign.offers) {
        for (let i = 0; i < campaign.offers.length; i++) {
          const index = campaign.offers[i].domainGroups.findIndex((el) => el.id === id);
          if (index !== -1) {
            campaign.offers[i].domainGroups[index] = {
              ...campaign.offers[i].domainGroups[index],
              status: domainGroup.status,
            };
          }
        }
      } else {
        if (campaign.domainGroups && campaign.domainGroups.length > 0) {
          const indexDomainInCampaign = campaign.domainGroups.findIndex((o) => o.id === id);
          if (~indexDomainInCampaign) {
            campaign.domainGroups[indexDomainInCampaign] = {
              ...campaign.domainGroups[indexDomainInCampaign],
              status: domainGroup.status,
            };
          }
        }
      }
    }

    yield put({ type: UPDATE_CAMPAIGN, history: tempHistory });
  }
}

function* refreshCampaign(data) {
  const { id } = data || {};
  try {
    const response = yield ServletServices.refreshCampaign({ id });
    if (response?.data === 'SUCCESS') {
      yield refreshServlet();
      yield put({ type: REQUEST_REFRESH_CAMPAIGN_SUCCESS, id });
      notifySuccess('Refresh campaign successfully!');
      return;
    }
  } catch (error) {
    yield put({ type: REQUEST_REFRESH_CAMPAIGN_FAILED, id });
    showError(error);
  }
}

function* resetToFailedContacts(data) {
  const { callbacks, body = {} } = data;
  try {
    const res = yield ServletServices.resendToTargetInteraction(body);
    const msg = isString(res?.data) && res?.data;
    if (msg === 'SUCCESS') {
      yield refreshServlet({ refreshPrevious: true });
      notifySuccess('Resend successfully!');
      callbacks && callbacks.onSuccess(res);
    } else {
      showError(msg);
      callbacks && callbacks.onFailed(msg);
    }
  } catch (error) {
    showError(error);
    callbacks && callbacks.onFailed(error);
  }
}

function* switchStatusRouteHistory(data) {
  const { callbacks, idHist = '', body = {} } = data;
  try {
    const response = yield ServletServices.updateMessageHist(body, idHist);
    const oldHistory = yield select((state) => state.servletReducer.historyPhone.history);

    if (isArray(oldHistory)) {
      const nextHistory = cloneDeep(oldHistory);
      const historyFounded = nextHistory.find((el) => idHist?.toString() === el?.id?.toString());
      if (historyFounded) {
        historyFounded.routes = body?.routes;
        yield put({ type: REQUEST_SWITCH_STATUS_ROUTE_HISTORY_SUCCESS, nextHistory });
      }
    }

    callbacks && callbacks.onSuccess(response);
    notifySuccess('Switch status route successfully!');
  } catch (error) {
    showError(error);
    yield put({ type: REQUEST_SWITCH_STATUS_ROUTE_HISTORY_FAILED });
    callbacks && callbacks.onFailed(error);
  }
}

function* sortingCampaign(data) {
  const { callbacks } = data;
  try {
    const { nextHistory, isLastHistory } = yield fetchMultiplePageHistory();
    callbacks && callbacks.onSuccess({ nextHistory, isLastHistory });
    yield put({ type: REQUEST_SORTING_CAMPAIGN_SUCCESS, nextHistory, isLastHistory });
  } catch (error) {
    showError(error);
    yield put({ type: REQUEST_SORTING_CAMPAIGN_FAILED });
    callbacks && callbacks.onFailed(error);
  }
}

function* updateStatusContactGroup(data) {
  const { callbacks, idHist = '', body = {} } = data;
  try {
    const response = yield ServletServices.updateMessageHist(body, idHist);
    const oldHistory = yield select((state) => state.servletReducer.historyPhone.history);

    if (isArray(oldHistory)) {
      const nextHistory = cloneDeep(oldHistory);
      const historyFounded = nextHistory.find((el) => idHist?.toString() === el?.id?.toString());

      if (historyFounded) {
        if (isArray(historyFounded?.contactGroups)) {
          const newStatusOfContactGroups = body.contactGroups.reduce((obj, el) => {
            return {
              ...obj,
              [el.id]: el.status,
            };
          }, {});

          historyFounded.contactGroups.forEach((contact) => {
            contact.status = newStatusOfContactGroups[contact.id];
          });
        }
        yield put({ type: REQUEST_UPDATE_STATUS_CONTACT_GROUP_SUCCESS, nextHistory });
      }
    }

    callbacks && callbacks.onSuccess(response);
    notifySuccess('Switch status of contact group successfully!');
  } catch (error) {
    showError(error);
    yield put({ type: REQUEST_UPDATE_STATUS_CONTACT_GROUP_FAILED });
    callbacks && callbacks.onFailed(error);
  }
}

//! Watcher
export default function* watchGetAPIFunction() {
  yield takeEvery(REQUEST_PAUSE_LOAD_HISTORY, pauseLoadHistory);
  yield takeLatest(
    REQUEST_API_SERVLET,
    handleWorker({
      handler: getServlet,
      cancelled: take([REQUEST_REFRESH_CAMPAIGN, typeUnmounted(components.monitoring)]),
    })
  );
  yield takeLatest(
    REQUEST_REFRESH_SERVLET,
    handleWorker({
      handler: getServlet,
      cancelled: take([REQUEST_REFRESH_CAMPAIGN, typeUnmounted(components.monitoring)]),
    })
  );

  yield takeLatest(
    REQUEST_SORTING_CAMPAIGN,
    handleWorker({
      handler: sortingCampaign,
      cancelled: take([REQUEST_REFRESH_CAMPAIGN, typeUnmounted(components.monitoring)]),
    })
  );

  yield takeEvery(REQUEST_HISTORY_BY_ID, getServletById);
  yield takeLatest(REQUEST_SEND_INBOX_SERVLET, sendInboxServlet);
  yield takeLatest(REQUEST_SEND_SERVLET, sendServlet);
  yield takeLatest(REQUEST_SPIN_SERVLET, spinServlet);
  yield takeLatest(
    REQUEST_PREVIEW_SPIN,
    handleWorker({
      handler: previewSpinTax,
      cancelled: take([REQUEST_CLEAR_PREVIEW_SPIN]),
    })
  );

  yield takeLatest(REQUEST_RESEND_TO_FAILED_CONTACTS, resetToFailedContacts);
  yield takeLatest(REQUEST_MORE_HISTORY, getMoreHistory);
  yield takeEvery(REQUEST_CONTINUE_HIST, continueHist);
  yield takeEvery(REQUEST_MARK_AS_COMPLETED, markAsCompleted);
  yield takeEvery(REQUEST_UPDATE_MESSAGE_HIST, updateMessageHist);
  yield takeLatest(REQUEST_FILTER_CAMPAIGN, getServlet);
  yield takeLatest(REQUEST_PULL_REVENUE_CAMPAIGN, pullRevenue);
  // yield takeLatest(REQUEST_PHONE_NUMBERS, getPhoneNumbers);
  yield takeEvery(REQUEST_UPDATE_STATUS_CONTACT_GROUP, updateStatusContactGroup);

  // yield takeEvery(REQUEST_SEND_SERVLET_SUCCESS, getPhoneNumbers);
  // yield takeEvery(REQUEST_PAUSE_LOAD_HISTORY_SUCCESS, getPhoneNumbers);
  // yield takeEvery(REQUEST_MARK_AS_COMPLETED_SUCCESS, getPhoneNumbers);
  // yield takeEvery(REQUEST_CONTINUE_HIST_SUCCESS, getPhoneNumbers);
  yield takeEvery(
    REQUEST_SWITCH_STATUS_ROUTE_HISTORY,
    handleWorker({
      handler: switchStatusRouteHistory,
      cancelled: take([CHANGE_FILTER_CAMPAIGN]),
    })
  );

  yield takeLatest(
    REQUEST_REFRESH_CAMPAIGN,
    handleWorker({
      handler: refreshCampaign,
      cancelled: take([CHANGE_FILTER_CAMPAIGN]),
    })
  );

  // Listen event, if updating status is successfully, executing updateWhenChangeStatusCreative()
  yield takeEvery(REQUEST_UPDATE_CREATIVE_CAMPAIGN_SUCCESS, updateWhenChangeStatus);
  yield takeEvery(REQUEST_UPDATE_OFFER_CAMPAIGN_SUCCESS, updateWhenChangeStatus);
  yield takeEvery(REQUEST_UPDATE_DOMAIN_CAMPAIGN_SUCCESS, updateWhenChangeStatus);
}
